
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { app } from "../firebase-config.js";
import { locale } from '../services/LocaleService.js';

const db = getFirestore(app);

export const ReportModal = {
    show: (options = {}) => {
        // Options: { source: 'general'|'enom', feedId: string, targetUserId: string }
        const source = options.source || 'general';
        const feedContext = options.feedId ? `[Feed: ${options.feedId}]` : '';

        // Overlay
        // [FIX] Use Standard Z-Index (9990) for compatibility with ModalManager
        const overlay = document.createElement('div');
        overlay.className = "fixed inset-0 bg-black/60 z-[9990] flex items-center justify-center fade-in backdrop-blur-sm p-4";

        const modal = document.createElement('div');
        modal.className = "bg-white dark:bg-zinc-900 w-full max-w-md rounded-2xl shadow-xl overflow-hidden animate-slide-up";

        modal.innerHTML = `
            <div class="p-5 border-b border-gray-100 dark:border-zinc-800 flex justify-between items-center">
                <h3 class="font-bold flex items-center gap-2 text-gray-800 dark:text-gray-100">
                    <span class="material-symbols-outlined text-red-500">report</span>
                    ${locale.t('report.title')}
                </h3>
                <button id="btn-close-report" class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors">
                    <span class="material-symbols-outlined text-gray-400">close</span>
                </button>
            </div>
            <div class="p-6 space-y-4">
                <div class="space-y-1">
                    <label class="text-xs font-bold text-gray-500 uppercase">${locale.t('report.category_label')}</label>
                    <div class="relative">
                        <select id="report-category" class="w-full p-3 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl appearance-none focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all">
                            <option value="general">${locale.t('report.categories.general')}</option>
                            <option value="spam">${locale.t('report.categories.spam')}</option>
                            <option value="abuse">${locale.t('report.categories.abuse')}</option>
                            <option value="adult">${locale.t('report.categories.adult')}</option>
                            <option value="bug">${locale.t('report.categories.bug')}</option>
                        </select>
                        <span class="material-symbols-outlined absolute right-3 top-3.5 text-gray-400 pointer-events-none">expand_more</span>
                    </div>
                </div>

                <div class="space-y-1">
                    <label class="text-xs font-bold text-gray-500 uppercase">${locale.t('report.content_label')}</label>
                    <textarea id="report-content" rows="4" class="w-full p-3 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none transition-all placeholder:text-gray-400 dark:text-white" placeholder="${locale.t('report.placeholder_content')}"></textarea>
                </div>

                <div class="space-y-1">
                    <label class="text-xs font-bold text-gray-500 uppercase">${locale.t('report.url_label')}</label>
                    <input type="text" id="report-url" value="${window.location.href}" class="w-full p-3 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-500 text-sm focus:ring-2 focus:ring-red-500 outline-none transition-all" readonly>
                </div>

                <div class="pt-2">
                    <button id="btn-submit-report" class="w-full py-3.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl shadow-lg shadow-red-500/20 transition-all flex items-center justify-center gap-2 active:scale-[0.98]">
                        <span class="material-symbols-outlined">send</span>
                        ${locale.t('report.btn_submit')}
                    </button>
                </div>
            </div>
            <div class="px-6 pb-6">
                <p class="text-xs text-center text-gray-400 dark:text-gray-600">
                    ${locale.t('report.warning')}
                </p>
            </div>
        `;

        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        // Handlers
        const close = () => {
            overlay.classList.add('animate-fade-out');
            modal.classList.add('animate-slide-down');
            setTimeout(() => overlay.remove(), 200);
        };

        modal.querySelector('#btn-close-report').onclick = close;
        overlay.onclick = (e) => {
            if (e.target === overlay) close();
        };

        // ... 

        const btnSubmit = modal.querySelector('#btn-submit-report');
        btnSubmit.onclick = async () => {
            if (!window.user) {
                // [FIX] Close self before showing AuthRequireModal to prevent stacking
                close();

                // [FIX] Report requires login strictly or shows AuthModal
                const { AuthRequireModal } = await import('./modals/AuthRequireModal.js');
                const { ModalManager } = await import('../utils/ModalManager.js');
                ModalManager.show(AuthRequireModal);
                return;
            }

            const category = modal.querySelector('#report-category').value;
            const content = modal.querySelector('#report-content').value.trim();
            const url = modal.querySelector('#report-url').value.trim();

            if (!content) {
                alert(locale.t('common.error')); // Fallback
                return;
            }

            try {
                btnSubmit.disabled = true;
                btnSubmit.innerText = locale.t('common.loading');

                await addDoc(collection(db, 'reports'), {
                    category,
                    content,
                    status: 'open',
                    userId: window.user.uid,
                    userEmail: window.user.email,
                    createdAt: serverTimestamp(),
                    url: url,
                    source: source, // 'enom' or 'general'
                    feedId: options.feedId || null,
                    targetUserId: options.targetUserId || null
                });

                // Success UI Replace
                modal.innerHTML = `
                    <div class="p-8 flex flex-col items-center text-center animate-fade-in">
                        <div class="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                            <span class="material-symbols-outlined text-4xl">check_circle</span>
                        </div>
                        <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">${locale.t('report.success_title')}</h3>
                        <p class="text-gray-500 text-sm mb-6">${locale.t('report.success_desc')}</p>
                        <button id="btn-close-success" class="w-full py-3 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 text-white font-bold rounded-xl transition-colors">
                            ${locale.t('common.close')}
                        </button>
                    </div>
                `;

                modal.querySelector('#btn-close-success').onclick = close;

            } catch (e) {
                console.error("Report Error:", e);
                alert(locale.t('common.error'));
                btnSubmit.disabled = false;
                btnSubmit.innerHTML = `<span class="material-symbols-outlined">send</span> ${locale.t('report.btn_submit')}`;
            }
        };
    }
};
